import React from 'react';
import { useTranslation } from 'react-i18next';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
  ChartBarIcon as StatsIcon,
  GlobeIcon,
  LinkIcon as ChainIcon,
  SupportIcon as ResourcesIcon,
  TrendingUpIcon as PortfolioIcon,
  UserGroupIcon as CommunityIcon,
  ViewGridIcon as DappsIcon,
} from '@heroicons/react/outline';

import { AumGlobal } from 'components/Aum';
import { Card, ChainCards, CurrencyToggleCard } from 'components/Card';
import { Community } from 'components/Community';
import { Dapps } from 'components/Dapps';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { PolygonTreasuryTotal } from 'components/PolygonTreasury';
import { PortfolioCards } from 'components/Portfolio';
import { Resources } from 'components/Resources';
import { Section } from 'components/Section';
import { useChain, usePrice, useSettings, useStats } from 'contexts';
import { CHAIN_DATA_MAPPING } from 'utils/constants';
import { formatCount } from 'utils/number';
import { ChainId, Currency } from 'utils/types';
import PolywaveBanner from './PolywaveBanner';

const HomePage = () => {
  const { t } = useTranslation();
  const { id: chainId, shortDisplayName, styleSlug } = useChain();
  const { isDarkMode } = useSettings();
  const {
    currentStats: { totalSupply, totalBurnt = 0, totalLocked, lpPrice },
    globalSupply,
    globalBurnt,
    [ChainId.EthMainnet]: { totalSupply: totalSupplyEth },
    [ChainId.BscMainnet]: { totalSupply: totalSupplyBsc },
    [ChainId.XDai]: { totalSupply: totalSupplyXDai },
    [ChainId.MaticMainnet]: { totalSupply: totalSupplyMatic },
  } = useStats();
  const { ethTokenPrices, bscTokenPrices, xDaiTokenPrices, maticTokenPrices } = usePrice();
  const ethSurfUsd = ethTokenPrices[Currency.SURF]?.[Currency.USD];
  const bscSurfUsd = bscTokenPrices[Currency.SURF]?.[Currency.USD];
  const xDaiSurfUsd = xDaiTokenPrices[Currency.SURF]?.[Currency.USD];
  const maticSurfUsd = maticTokenPrices[Currency.SURF]?.[Currency.USD];
  const globalMarketCap =
    ethSurfUsd &&
    typeof bscSurfUsd !== 'undefined' &&
    xDaiSurfUsd &&
    maticSurfUsd &&
    totalSupplyEth &&
    totalSupplyBsc &&
    totalSupplyXDai &&
    totalSupplyMatic
      ? ethSurfUsd * totalSupplyEth +
        bscSurfUsd * totalSupplyBsc +
        xDaiSurfUsd * totalSupplyXDai +
        maticSurfUsd * totalSupplyMatic
      : undefined;

  return (
    <div className="container space-y-10">
      <PolywaveBanner />
      <Section title={t('home.section_header_chains')} icon={ChainIcon}>
        <ChainCards />
      </Section>
      <Section
        title={t('home.section_header_stats', { chain: shortDisplayName })}
        icon={StatsIcon}
        actionTo="/stats"
        actionTitle={t('home.section_action_global_stats')}
      >
        {chainId === ChainId.MaticMainnet && <PolygonTreasuryTotal />}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
          <CurrencyToggleCard
            title={t('global_stats.market_cap')}
            chainId={chainId}
            amount={totalSupply}
            defaultQuoteCurrency={Currency.USD}
            hideSurfOption
          />
          <CurrencyToggleCard title={t('global_stats.token_price')} chainId={chainId} amount={1} />
          <Card
            title={t('global_stats.total_supply')}
            body={
              <>
                {totalSupply ? formatCount(totalSupply - totalBurnt) : <LoadingIndicator />}
                <span className="pl-1 text-xl text-gray-999">SURF</span>
              </>
            }
            footer={
              !!totalSupply &&
              !!totalLocked && (
                <div className="uppercase">
                  {t('global_stats.tokens_circulating', {
                    total: formatCount(totalSupply - totalLocked),
                  })}
                </div>
              )
            }
          />
          {(chainId === ChainId.EthMainnet || chainId === ChainId.MaticMainnet) && (
            <CurrencyToggleCard
              title={t('global_stats.lp_token_price')}
              chainId={chainId}
              amount={lpPrice}
              baseCurrency={Currency.ETH}
              defaultQuoteCurrency={Currency.USD}
            />
          )}
        </div>
      </Section>
      <Section
        className="space-y-4"
        title={t('global_stats.title', { chain: shortDisplayName })}
        icon={GlobeIcon}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
          <Card
            title={t('global_stats.global_market_cap')}
            body={globalMarketCap ? `$${formatCount(globalMarketCap)}` : <LoadingIndicator />}
            filled
          />
          <Card
            title={t('global_stats.global_supply')}
            body={
              <>
                {globalSupply && globalBurnt ? (
                  formatCount(globalSupply - globalBurnt)
                ) : (
                  <LoadingIndicator />
                )}
                <span className={`pl-1 text-xl text-${styleSlug}-light`}>SURF</span>
              </>
            }
            footer={
              !!globalSupply &&
              !!globalBurnt && (
                <div className="uppercase">
                  {t('global_stats.global_supply_minted', {
                    total: formatCount(globalSupply),
                  })}{' '}
                  |{' '}
                  {t('global_stats.global_supply_burnt', {
                    total: formatCount(globalBurnt),
                  })}
                </div>
              )
            }
            filled
          />
        </div>

        <AumGlobal />

        <Card
          title={t('home.total_supply_distribution')}
          body={
            <>
              <HighchartsReact
                highcharts={Highcharts}
                options={
                  {
                    credits: false,
                    chart: {
                      type: 'pie',
                      backgroundColor: 'transparent',
                    },
                    title: {
                      text: '',
                    },
                    tooltip: {
                      pointFormatter: function () {
                        return `${formatCount(this.y || 0)} SURF`;
                      },
                    },
                    plotOptions: {
                      pie: {
                        colors: [
                          CHAIN_DATA_MAPPING[ChainId.EthMainnet].color,
                          CHAIN_DATA_MAPPING[ChainId.MaticMainnet].color,
                          CHAIN_DATA_MAPPING[ChainId.BscMainnet].color,
                          CHAIN_DATA_MAPPING[ChainId.XDai].color,
                        ],
                        cursor: 'pointer',
                        dataLabels: {
                          enabled: true,
                          format: '<b>{point.name}</b>: {point.percentage:.2f}%',
                          color: isDarkMode ? '#fff' : '#000',
                        },
                      },
                    },
                    series: [
                      {
                        name: 'SURF',
                        type: 'pie',
                        data: [
                          {
                            name: CHAIN_DATA_MAPPING[ChainId.EthMainnet].shortDisplayName,
                            y: totalSupplyEth,
                            sliced: true,
                          },
                          {
                            name: CHAIN_DATA_MAPPING[ChainId.MaticMainnet].shortDisplayName,
                            y: totalSupplyMatic,
                          },
                          {
                            name: CHAIN_DATA_MAPPING[ChainId.XDai].shortDisplayName,
                            y: totalSupplyXDai,
                          },
                          {
                            name: CHAIN_DATA_MAPPING[ChainId.BscMainnet].shortDisplayName,
                            y: totalSupplyBsc,
                          },
                          {
                            name: t('home.burnt'),
                            y: globalBurnt,
                          },
                        ],
                      },
                    ],
                  } as Highcharts.Options
                }
              />
            </>
          }
        />
      </Section>
      <Section
        title={t('portfolio.title')}
        icon={PortfolioIcon}
        actionTo="/portfolio"
        actionTitle={t('home.section_action_portfolio')}
      >
        <PortfolioCards />
      </Section>

      <Section
        title={t('dapps.title')}
        icon={DappsIcon}
        actionTo="https://docs.surf.finance/core-dapps"
        actionTitle={t('home.section_action_dapps')}
      >
        <Dapps />
      </Section>

      <Section title={t('home.section_header_community')} icon={CommunityIcon}>
        <Community />
      </Section>

      <Section
        title={t('home.section_header_resources')}
        icon={ResourcesIcon}
        actionTo="https://docs.surf.finance/"
        actionTitle={t('home.section_action_resources')}
      >
        <Resources />
      </Section>
    </div>
  );
};

export default HomePage;
